import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  FEATURE_KEYS,
  emptyFeatureFlags,
  loadKnownFeatureFlags,
} from '../utils/featureFlags';

const KNOWN_KEYS = [FEATURE_KEYS.LIVE_TV, FEATURE_KEYS.WEB_STORE];
/** Re-check flags while the tab stays open (module TTL avoids duplicate network). */
const REFRESH_INTERVAL_MS = 60_000;

const FeatureFlagsContext = createContext({
  ready: false,
  flags: emptyFeatureFlags(),
  records: {},
  isEnabled: () => false,
  getFlag: () => null,
  refresh: () => Promise.resolve(),
});

export function FeatureFlagsProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [flags, setFlags] = useState(emptyFeatureFlags);
  const [records, setRecords] = useState({});

  const applyFlags = useCallback((next, nextRecords) => {
    setFlags({
      [FEATURE_KEYS.LIVE_TV]: Boolean(next[FEATURE_KEYS.LIVE_TV]),
      [FEATURE_KEYS.WEB_STORE]: Boolean(next[FEATURE_KEYS.WEB_STORE]),
    });
    setRecords(nextRecords || {});
    setReady(true);
  }, []);

  const refresh = useCallback(
    (opts = {}) =>
      loadKnownFeatureFlags(KNOWN_KEYS, opts)
        .then(({ flags: next, records: nextRecords }) => {
          applyFlags(next, nextRecords);
        })
        .catch(() => {
          setReady(true);
        }),
    [applyFlags],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Pick up CRM changes while the tab stays open (deduped by module TTL).
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, REFRESH_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(intervalId);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      ready,
      flags,
      records,
      isEnabled: (key) => Boolean(flags[key]),
      getFlag: (key) => getFlagRecord(records, key),
      refresh,
    }),
    [ready, flags, records, refresh]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

function getFlagRecord(records, key) {
  if (!key) return null;
  return records[String(key).toUpperCase()] || null;
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}
